import {
  Box,
  Button,
  Flex,
  Icon,
  Link,
  SearchInput,
  Stack,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import {
  DataGrid,
  DATA_GRID_CHECKBOX_SELECTION_COLUMN_DEF,
  ColumnDef,
} from '@tonic-one/react-data-grid';
import {
  AddIcon,
  CloseSIcon,
  ErrorIcon,
  SuccessIcon,
} from '@tonic-one/react-icons';
import { memo, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import DelModal from 'src/pages/Report/ActionModal/del';
import { AdministratorProps } from '@Types/administrator';
import CSpinner from '@Components/CSpinner';
import ErrorAlert from '@Components/Common/ErrorAlert';
import IconButton from '@Components/IconButton';
import TextEllipsis from '@Components/Common/Tooltip/TextEllipsis';
import { Info } from '@Components/Common/Info';
import { useNavigate } from 'react-router-dom';
import { AdministrationApi, GlobalApi } from '@Apis';
import { PermissionNameEnum } from '@Types/permissions';
import { usePermissionStore } from '@Models/usePermissionModel';
import AlertModal from '@Components/Common/Modal/AlertModal';
import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from 'src/QueryClient';
import { useLoginUserStore } from '@Models/useLoginUserModel';

const Administrators = memo(() => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { isPending: isGetSsoConfigPending, data: ssoConfigData } = useQuery({
    queryKey: ['getSsoConfig'],
    queryFn: async () => {
      return (await GlobalApi.getSsoConfig()).data;
    },
  });
  const { user } = useLoginUserStore();
  const { isPending, data: localAccountsData } = useQuery({
    queryKey: ['getLocalAccounts'],
    queryFn: async () => {
      return (await AdministrationApi.getLocalAccounts()).data;
    },
  });
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
  const { t } = useTranslation();
  const [delModal, setDelModal] = useState<{
    isOpen: boolean;
    title: string;
    content: JSX.Element[] | undefined;
  }>({
    isOpen: false,
    title: '',
    content: undefined,
  });
  const [resetPasswordAlert, setResetPasswordAlert] = useState<{
    title: string;
    content: JSX.Element;
  }>();
  const [deleteMultiTenantAlert, setDeleteMultiTenantAlert] = useState<{
    title: string;
    content: JSX.Element;
  }>();
  const { widthEditablePermission } = usePermissionStore();
  const navigate = useNavigate();
  const [searchKeyword, setSearchKeyword] = useState('');
  const filteredData = useMemo(() => {
    const tempData = localAccountsData?.map((item) => {
      if (!ssoConfigData?.enableSamlSso) {
        item.ssoEnabled = 0;
      }
      return item;
    });
    if (!searchKeyword) return tempData;
    return tempData?.filter((item) =>
      ['email', 'roleName', 'userDisplayname'].some((field) =>
        item[field].toLowerCase().includes(searchKeyword.toLowerCase())
      )
    );
  }, [localAccountsData, searchKeyword, ssoConfigData]);

  const isRowSelectable = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ({ row }: { row: { value: AdministratorProps } }) => {
      return widthEditablePermission(PermissionNameEnum.administrator);
    },
    [widthEditablePermission]
  );

  const getRoleDisplayText = useCallback(
    (admin: AdministratorProps) => {
      if (admin.isInheritUser) {
        return t('administration.rbac.label.inherited');
      } else if (admin.roleName === 'Administrator') {
        return t('administration.rbac.label.defaultRole');
      } else {
        return admin.roleName;
      }
    },
    [t]
  );

  const columns: ColumnDef[] = useMemo(
    () => [
      {
        ...DATA_GRID_CHECKBOX_SELECTION_COLUMN_DEF,
        width: '5%',
        // minWidth: 48,
      },
      {
        id: 'userDisplayname',
        header: (
          <TextEllipsis popperProps={{ usePortal: true }}>
            {t('common.label.enterName')}
          </TextEllipsis>
        ),
        accessorKey: 'userDisplayname',
        width: '20%',
        // minWidth: 150,
        cell: ({ row }: { row: { value: AdministratorProps } }) => {
          const admin = row.value;
          return (
            <Link
              maxWidth="100%"
              onClick={() => {
                navigate(
                  `/administration/user-management/edit-user/${admin.email}`
                );
              }}
              disabled={
                !widthEditablePermission(PermissionNameEnum.administrator)
              }
            >
              <TextEllipsis popperProps={{ usePortal: true }}>
                {admin.userDisplayname}
              </TextEllipsis>
            </Link>
          );
        },
      },
      {
        id: 'email',
        header: (
          <TextEllipsis popperProps={{ usePortal: true }}>
            {t('common.label.emailAddress')}
          </TextEllipsis>
        ),
        accessorKey: 'email',
        width: '25%',
        // minWidth: 250,
        cell: ({ row }: { row: { value: AdministratorProps } }) => (
          <TextEllipsis popperProps={{ usePortal: true }}>
            {row.value.email}
          </TextEllipsis>
        ),
      },
      {
        id: 'tenantSwitchable',
        header: () => (
          // Text followed immediately by info icon. Text truncates when shrunk but icon never disappears
          <Flex alignItems="center" minWidth={0}>
            <Box mr={8} flex="0 1 auto" minWidth={0}>
              <TextEllipsis popperProps={{ usePortal: true }}>
                {t('common.label.multiTenantSwitching')}
              </TextEllipsis>
            </Box>
            <Box flex="0 0 auto">
              <Info content={t('administration.rbac.multiTenant.desc')} />
            </Box>
          </Flex>
        ),
        accessorKey: 'tenantSwitchable',
        width: '15%',
        // minWidth: 190,
        cell: ({ row }: { row: { value: AdministratorProps } }) => (
          <TextEllipsis popperProps={{ usePortal: true }}>
            {row.value.tenantSwitchable
              ? t(`common.action.label.yes`)
              : t('common.action.label.no')}
          </TextEllipsis>
        ),
      },
      {
        id: 'roleName',
        header: (
          <TextEllipsis popperProps={{ usePortal: true }}>
            {t('common.label.role')}
          </TextEllipsis>
        ),
        accessorKey: 'roleName',
        sortingFn: (a: any, b: any) => {
          const roleA = a.row.value;
          const roleB = b.row.value;

          const displayNameA = getRoleDisplayText(roleA);
          const displayNameB = getRoleDisplayText(roleB);

          return displayNameA.localeCompare(displayNameB, undefined, {
            numeric: true,
            sensitivity: 'base',
          });
        },
        width: '15%',
        // minWidth: 190,
        cell: ({ row }: { row: { value: AdministratorProps } }) => {
          const role = row.value;
          const displayText = getRoleDisplayText(role);

          return (
            <Text>
              {role.isInheritUser ? (
                <Flex alignItems="center" minWidth={0}>
                  <Box mr={8} flex="0 1 auto" minWidth={0}>
                    <TextEllipsis popperProps={{ usePortal: true }}>
                      {displayText}
                    </TextEllipsis>
                  </Box>
                  <Box flex="0 0 auto">
                    <Info
                      content={t('administration.rbac.label.inherited.desc')}
                    />
                  </Box>
                </Flex>
              ) : (
                <TextEllipsis popperProps={{ usePortal: true }}>
                  {displayText}
                </TextEllipsis>
              )}
            </Text>
          );
        },
      },
      {
        id: 'ssoEnabled',
        header: (
          <TextEllipsis popperProps={{ usePortal: true }}>
            {t('common.label.ssoToConsole')}
          </TextEllipsis>
        ),
        accessorKey: 'ssoEnabled',
        sortingFn: (a: any, b: any) => {
          const adminA = a.row.value;
          const adminB = b.row.value;

          // Inherited users ('-') go to the end, then sort by ssoEnabled (true before false)
          return (
            adminA.isInheritUser - adminB.isInheritUser ||
            adminB.ssoEnabled - adminA.ssoEnabled
          );
        },
        width: '10%',
        // minWidth: 150,
        cell: ({ row }: { row: { value: AdministratorProps } }) => (
          <Text>
            {row.value.isInheritUser ? (
              <TextEllipsis popperProps={{ usePortal: true }}>-</TextEllipsis>
            ) : (
              <Flex align="center" gap="2x">
                {row.value.ssoEnabled ? (
                  <Icon as={SuccessIcon} color="#04C45A" />
                ) : (
                  <Icon as={ErrorIcon} color="#F24C4F" />
                )}
              </Flex>
            )}
          </Text>
        ),
      },
      {
        id: 'verifiedEmail',
        header: (
          <TextEllipsis popperProps={{ usePortal: true }}>
            {t('common.label.emailVerified')}
          </TextEllipsis>
        ),
        accessorKey: 'verifiedEmail',
        sortingFn: (a: any, b: any) => {
          const adminA = a.row.value;
          const adminB = b.row.value;

          // Inherited users ('-') go to the end, then sort by verifiedEmail (true before false)
          return (
            adminA.isInheritUser - adminB.isInheritUser ||
            adminB.verifiedEmail - adminA.verifiedEmail
          );
        },
        width: '10%',
        // minWidth: 150,
        cell: ({ row }: { row: { value: AdministratorProps } }) => (
          <Text>
            {row.value.isInheritUser ? (
              <TextEllipsis popperProps={{ usePortal: true }}>-</TextEllipsis>
            ) : (
              <Flex align="center" gap="2x">
                {row.value.verifiedEmail ? (
                  <Icon as={SuccessIcon} color="#04C45A" />
                ) : (
                  <Icon as={ErrorIcon} color="#F24C4F" />
                )}
              </Flex>
            )}
          </Text>
        ),
      },
    ],
    [navigate, t, widthEditablePermission, getRoleDisplayText]
  );
  const [rowSelection, setRowSelection] = useState({});
  const selectedRowCount = Object.keys(rowSelection).length;
  const handleReset = () => {
    const selectedRows = getSelectedRows();
    const content = selectedRows.map((item) => (
      <Text key={item.sha1} mb={8}>
        {item.email}
      </Text>
    ));
    setResetPasswordAlert({
      title: t('administration.rbac.resetPassword.alertMsg'),
      content: <Box>{content}</Box>,
    });
  };

  const handleDel = () => {
    const selectedRows = getSelectedRows();
    const content = selectedRows.map((item) => (
      <Text key={item.email} mb={8}>
        {item.email}
      </Text>
    ));
    setDelModal({
      isOpen: true,
      title: t('administration.rbac.delete.alertMsg'),
      content,
    });
  };

  const resetTableFilters = () => {
    setRowSelection({});
    setSearchKeyword('');
  };

  function getSelectedRows() {
    const selectedRows: AdministratorProps[] = [];
    for (const key of Object.keys(rowSelection)) {
      const item = localAccountsData?.find((item) => item.email === key);
      if (item) {
        selectedRows.push(item);
      }
    }
    return selectedRows;
  }

  const resetPasswordMutation = useMutation({
    mutationFn: async (email: string) =>
      AdministrationApi.resetPasswordByEmail(email),
    onSuccess: () => {
      resetTableFilters();
      window.showNotification({
        titleMsg: t('administration.rbac.resetPassword.successMsg'),
        severity: 'success',
      });
    },
    onError: () => {
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    },
  });

  const confirmReset = async () => {
    if (!selectedRowCount) return;
    const selectedRows = getSelectedRows();
    resetPasswordMutation.mutate(selectedRows[0].email);
  };

  // Mutations
  const deleteUserMutation = useMutation({
    mutationFn: async (email: string) =>
      AdministrationApi.deleteLocalAccountsByEmail(email),
    onSuccess: () => {
      setDelModal({
        isOpen: false,
        title: '',
        content: undefined,
      });
      resetTableFilters();
      window.showNotification({
        titleMsg: t('administration.rbac.delete.successMsg'),
        severity: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['getLocalAccounts'] });
      queryClient.invalidateQueries({ queryKey: ['getRoleByRoleId'] });
    },
    onError: (error: any) => {
      console.log('error', error);
      if (error?.data?.error?.details[0].reason === 'MultiTenant;FOUND') {
        const content = error?.data.error.details[0].clpInfos
          .split(',')
          .map((item: string) => (
            <Text key={item} mb={8}>
              {item}
            </Text>
          ));
        setDeleteMultiTenantAlert({
          title: t('administration.rbac.delete.multiTenantMsg'),
          content,
        });
      } else {
        window.showNotification({
          titleMsg: t('common.apiException.internalError'),
          severity: 'error',
        });
      }
      setDelModal({
        isOpen: false,
        title: '',
        content: undefined,
      });
    },
  });

  const confirmDel = async () => {
    if (!selectedRowCount) return;
    const selectedRows = getSelectedRows();
    deleteUserMutation.mutate(selectedRows[0].email);
  };
  return (
    <Flex flex={1}>
      {(isPending ||
        resetPasswordMutation.isPending ||
        deleteUserMutation.isPending ||
        isGetSsoConfigPending) && <CSpinner />}
      <Flex flex={1} direction="column">
        <DelModal
          visible={delModal.isOpen}
          onClose={() => {
            setDelModal({
              isOpen: false,
              title: '',
              content: undefined,
            });
          }}
          onConfirm={confirmDel}
          loading={deleteUserMutation.isPending}
          title={delModal.title}
          content={delModal.content}
          withEditPermission={widthEditablePermission(
            PermissionNameEnum.administrator
          )}
        ></DelModal>
        <AlertModal
          visible={!!resetPasswordAlert}
          onClose={() => {
            setResetPasswordAlert(undefined);
          }}
          onConfirm={async () => {
            await confirmReset();
            setResetPasswordAlert(undefined);
          }}
          confirmButtonName={t('common.button.ok')}
          title={resetPasswordAlert?.title}
          content={resetPasswordAlert?.content}
        />
        <AlertModal
          visible={!!deleteMultiTenantAlert}
          onClose={() => {
            setDeleteMultiTenantAlert(undefined);
          }}
          onConfirm={() => {
            setDeleteMultiTenantAlert(undefined);
          }}
          confirmButtonName={t('common.button.ok')}
          title={deleteMultiTenantAlert?.title}
          content={deleteMultiTenantAlert?.content}
        />
        {errorMsg && (
          <ErrorAlert
            errorMsg={errorMsg}
            onClose={() => setErrorMsg(undefined)}
          />
        )}
        <Flex mx="3x" my="4x" alignItems="center">
          {selectedRowCount !== 0 ? (
            <Stack direction="row" gap="3x">
              {!widthEditablePermission(PermissionNameEnum.administrator) ||
              selectedRowCount > 1 ||
              getSelectedRows()[0].email === user?.loginEmail ? null : (
                <Button variant="secondary" onClick={handleDel}>
                  {t('reportCommon.buttons.delete')}
                </Button>
              )}
              {!(
                getSelectedRows()[0].ssoEnabled ||
                getSelectedRows()[0].isInheritUser
              ) && (
                <Button
                  variant="secondary"
                  onClick={handleReset}
                  disabled={
                    !widthEditablePermission(
                      PermissionNameEnum.administrator
                    ) || selectedRowCount > 1
                  }
                >
                  {t('login.text.resetPassword')}
                </Button>
              )}
              <Flex alignItems="center">
                <IconButton onClick={resetTableFilters}>
                  <Icon as={CloseSIcon}></Icon>
                </IconButton>
                <Box lineHeight="2rem">
                  <Text color={colorStyle.color.tertiary}>
                    {selectedRowCount}&nbsp;
                    {t('common.label.selected')}
                  </Text>
                </Box>
              </Flex>
            </Stack>
          ) : null}
          {selectedRowCount === 0 ? (
            <Stack direction="row" gap="3x">
              <Button
                variant="primary"
                onClick={() => {
                  navigate('/administration/user-management/add-user');
                }}
                disabled={
                  !widthEditablePermission(PermissionNameEnum.administrator)
                }
              >
                <Icon as={AddIcon} mr={8} />
                {t('globalSetting.approvedUser.action.label.add')}
              </Button>
              <SearchInput
                onChange={(e: any) => {
                  setSearchKeyword(e.target.value);
                }}
                onClearInput={() => {
                  setSearchKeyword('');
                }}
                width="374px"
              />
            </Stack>
          ) : null}
        </Flex>
        <Flex flex={1}>
          <DataGrid
            data={filteredData || []}
            columns={columns}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            isRowSelectable={isRowSelectable}
            isColumnSortable
            isColumnResizable
            autoSorting
            getRowId={(row) => row.email}
            emptyContent={t('common.noData')}
            isLoading={
              isPending ||
              resetPasswordMutation.isPending ||
              deleteUserMutation.isPending ||
              isGetSsoConfigPending
            }
            disableAutoResetRowSelection
          />
        </Flex>
      </Flex>
    </Flex>
  );
});

export default Administrators;
